<template>
  <checkout-view
    v-if="course && order && user_profile"
    :course="course"
    :step="step"
    :isLoading="submitting"
  >
    <template #header>
      <template v-if="step === 1">
        <a
          v-if="course"
          :href="course.url"
          class="flex items-center text-gray-600 hover:text-gray-800 transition-colors transition-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
        >
          <left-arrow-icon aria-hidden="true" />
          <span class="pl-2.5">{{ t("back") }}</span>
        </a>
      </template>
      <template v-else-if="step === 2">
        <button
          @click="step = 1"
          type="button"
          class="flex items-center text-gray-600 hover:text-gray-800 transition-colors transition-200 bg-transparent border-0 cursor-pointer p-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
          :aria-label="t('back_to_options')"
        >
          <left-arrow-icon aria-hidden="true" />
          <span class="pl-2.5">{{ t("back") }}</span>
        </button>
      </template>
      <template v-else>
        <button
          @click="step = 2"
          type="button"
          class="flex items-center text-gray-600 hover:text-gray-800 transition-colors transition-200 bg-transparent border-0 cursor-pointer p-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
          :aria-label="t('back_to_profile')"
        >
          <left-arrow-icon aria-hidden="true" />
          <span class="pl-2.5">{{ t("back") }}</span>
        </button>
      </template>
    </template>
    <template #left>
      <template v-if="step === 1">
        <div class="flex-1">
          <div class="pb-8">
            <h1 class="text-3xl sm:text-5xl font-extrabold text-custom-text">
              {{ t("select_price_and_addons") }}
            </h1>
          </div>

          <!-- Course Settings (price selection) - always shown -->
          <checkout-course-settings
            v-if="order"
            v-model="order.booking_option_id"
            :course_settings="availableCourseSettings"
            :base_price="
              course.booking_type === 'combo'
                ? null
                : subscriptionCourseSetting?.amount ?? null
            "
          />

          <!-- Product Add-ons - only if products exist -->
          <checkout-products
            v-if="hasProducts"
            :products="products"
            :orderLines="order?.order_lines || []"
            :saving="productsSaving"
            @add-product="addProductToOrder"
            @remove-product="removeProductFromOrder"
            @swap-product="swapProductInOrder"
          />

          <!-- Loading state for products -->
          <div v-else-if="!productsLoaded" class="flex items-center justify-center py-12">
            <div class="text-center">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p class="text-custom-grey-1">{{ t("loading") }}</p>
            </div>
          </div>

          <!-- Continue button -->
          <div class="sm:pt-10">
            <checkout-action @click="step = 2">
              <span class="leading-none">{{ t("continue") }}</span>
              <svg class="svg-icon svg-chevron-right">
                <use
                  xlink:href="#chevron-right"
                  width="100%"
                  height="100%"
                ></use>
              </svg>
            </checkout-action>
          </div>
        </div>
      </template>
      <checkout-profile-form
        v-else-if="step === 2"
        :course="course"
        @loading="(v: boolean) => (submitting = v)"
        @done="step = 3"
      />
      <template v-else>
        <div class="flex-1">
          <div>
            <div class="pb-8">
              <h1 class="text-3xl sm:text-5xl font-extrabold text-custom-text">
                {{ t("payment") }}
              </h1>
            </div>
            <div>
              <form
                class="divide-y divide-custom-grey-7"
                @submit.prevent="payWithCard"
              >
                <checkout-payment-gateway
                  v-model="checkoutState.paymentGateway"
                  :showStripe="hasStripeGateway"
                  :showManual="monthlyBilling ? false : hasManualGateway"
                  :showVipps="monthlyBilling ? false : hasVippsGateway"
                  :showPaypal="hasPaypalGateway"
                  :showKustom="monthlyBilling ? false : hasKustomGateway"
                  :cards="cards"
                  v-model:selectedCardToken="selectedCardToken"
                >
                  <template #stripe-card>
                    <checkout-stripe-card
                      :stripe="stripe"
                      ref="stripeCard"
                    />
                  </template>
                </checkout-payment-gateway>

                <checkout-address
                  v-if="order"
                  :addressType="addressType"
                  v-model:addressID="addressID"
                  v-model:invoiceType="order.invoice_type"
                />
                <div>
                  <checkout-error
                    v-if="checkoutState.paymentError"
                    :message="checkoutState.paymentError"
                    :details="checkoutState.paymentErrorDetails ?? undefined"
                  />
                  <div class="pt-5 pb-5">
                    <div>
                      <div class="pt-4 sm:pt-7">
                        <div
                          class="border-2 border-gray-300 rounded-lg p-4 bg-gray-50 hover:border-primary transition-colors terms-checkbox-container"
                        >
                          <label
                            class="flex items-start cursor-pointer group"
                            for="agree-to-terms"
                          >
                            <input
                              id="agree-to-terms"
                              type="checkbox"
                              :value="true"
                              v-model="agreeToTerms"
                              class="sr-only peer"
                              :aria-label="t('agree_to_terms_aria')"
                            />
                            <span
                              class="custom-checkbox flex-none w-5 h-5 mt-0.5 border-2 border-gray-400 rounded bg-white transition-all duration-200 peer-checked:bg-primary peer-checked:border-primary peer-focus-visible:ring-4 peer-focus-visible:ring-primary peer-focus-visible:ring-opacity-50 flex items-center justify-center relative z-10"
                              aria-hidden="true"
                            >
                              <svg
                                v-if="agreeToTerms"
                                class="w-3.5 h-3.5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="3"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </span>
                            <span class="ml-3 flex-1">
                              <i18n-t
                                keypath="agree_terms_label"
                                tag="span"
                                class="text-base text-custom-grey-1 font-medium"
                              >
                                <template v-slot:link>
                                  <a
                                    href="/terms-and-conditions"
                                    target="_blank"
                                    class="text-primary hover:text-primary-700 underline font-semibold"
                                    rel="noopener noreferrer"
                                    >{{ t("terms_and_conditions") }}</a
                                  >
                                </template>
                              </i18n-t>
                            </span>
                          </label>
                        </div>
                      </div>
                      <checkout-action :disabled="!agreeToTerms">
                        <span
                          v-if="checkoutState.paymentGateway === 'stripe'"
                          class="leading-none"
                        >
                          {{ t("pay_with_credit_card") }}
                        </span>
                        <span
                          v-else-if="checkoutState.paymentGateway === 'manual'"
                          class="leading-none"
                        >
                          {{ t("send_invoice") }}
                        </span>
                        <span
                          v-else-if="checkoutState.paymentGateway === 'vipps'"
                          class="leading-none"
                        >
                          {{ t("pay_with_vipps") }}
                        </span>
                        <span
                          v-else-if="checkoutState.paymentGateway === 'paypal'"
                          class="leading-none"
                        >
                          {{ t("pay_with_paypal") }}
                        </span>
                        <span
                          v-else-if="checkoutState.paymentGateway === 'kustom'"
                          class="leading-none"
                        >
                          {{ t("pay_with_kustom") }}
                        </span>
                        <span v-else class="leading-none">
                          {{ t("pay") }}
                        </span>
                        <svg class="svg-icon svg-chevron-right">
                          <use
                            xlink:href="#chevron-right"
                            width="100%"
                            height="100%"
                          ></use>
                        </svg>
                      </checkout-action>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </template>
    </template>
    <template #right>
      <checkout-course-info
        v-if="course && order && selectedCourseSetting"
        :course="course"
        :order="order"
        :course_setting="selectedCourseSetting"
        :groups="groups"
        :base_price="
          course.booking_type === 'combo' || !monthlyBilling
            ? null
            : subscriptionCourseSetting?.amount || null
        "
        v-model:selectedGroupID="order.group_id"
        v-model:startDate="order.start_date"
      />
      <checkout-coupon v-if="order" />
    </template>
  </checkout-view>
  <checkout-loader v-else />
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import {
  createCard,
  getCardsList,
  getCourseGroups,
  getCourseProducts,
  submitOrder,
} from "@/scripts/application/api";
import { useRoute, useRouter } from "vue-router";
import type { Stripe, StripeConstructorOptions } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useI18n } from "vue-i18n";
import type { AxiosError } from "axios";
import axios from "axios";
import { CheckoutView } from "@/scripts/application/views";
import {
  CheckoutAction,
  CheckoutAddress,
  CheckoutCoupon,
  CheckoutCourseInfo,
  CheckoutCourseSettings,
  CheckoutError,
  CheckoutPaymentGateway,
  CheckoutProducts,
  CheckoutProfileForm,
  CheckoutStripeCard,
  LeftArrowIcon,
} from "@/scripts/application/components";
import type {
  ICard,
  ICourseSetting,
  IOrder,
  IOrderLine,
  IProduct,
} from "@/scripts/application/types";
import { groups } from "@/scripts/application/stores/checkout";
import { trackCheckoutEvent } from "@/scripts/application/checkoutEventTracker";

import {
  useUserProfile,
  useCourse,
  useOrder,
  useAddresses,
  usePaymentGateways,
} from "@/scripts/application/composables";
import CheckoutLoader from "@/scripts/application/components/CheckoutLoader.vue";

type tCheckoutState = {
  step: number;
  loaded: boolean;
  paymentGateway: "stripe" | "manual" | "invoice" | "vipps" | "paypal" | "kustom";
  paymentError: string | null;
  paymentErrorDetails: string | null;
};

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const { saveAddress } = useAddresses();
const { course, fetchCourse } = useCourse();
const { order, fetchOrder, updateOrder: updateCourseOrder } = useOrder();
const { paymentGateways, fetchPaymentGateways } = usePaymentGateways();
const { user_profile, fetchUserProfile } = useUserProfile();

const checkoutState = reactive<tCheckoutState>({
  step: 1,
  loaded: false,
  paymentError: null,
  paymentErrorDetails: null,
  paymentGateway: "stripe",
});

// Every user-visible payment error funnels through checkoutState.paymentError,
// so a single watcher reports them all to the checkout events log.
watch(
  () => checkoutState.paymentError,
  (message) => {
    if (!message) return;
    trackCheckoutEvent("client_error_shown", {
      severity: "error",
      message,
      context: {
        gateway: checkoutState.paymentGateway,
        details: checkoutState.paymentErrorDetails,
      },
      orderUid: (route.params.order_uid as string) ?? null,
      flow: "course",
    });
  }
);

// Restore saved payment gateway from order on initial load, or default to first available
{
  let gatewayInitialized = false;
  watch([paymentGateways, order], ([gateways, currentOrder]) => {
    if (!gateways?.length || gatewayInitialized) return;

    // If the order has a saved payment_gateway_id, restore that selection
    if (currentOrder?.payment_gateway_id) {
      const savedGateway = gateways.find((gw) => gw.id === currentOrder.payment_gateway_id);
      if (savedGateway) {
        checkoutState.paymentGateway = savedGateway.kind as tCheckoutState["paymentGateway"];
        gatewayInitialized = true;
        return;
      }
    }

    // Fall back to first available if current selection isn't valid
    const currentAvailable = gateways.some((gw) => gw.kind === checkoutState.paymentGateway);
    if (!currentAvailable) {
      checkoutState.paymentGateway = gateways[0].kind as tCheckoutState["paymentGateway"];
    }

    if (currentOrder) gatewayInitialized = true;
  });
}
const stripe = ref<Stripe | null>();
const step = computed({
  get() {
    return route.query.step ? parseInt(route.query.step as string) : 1;
  },
  set(value) {
    router.push({ query: { step: value } });
  },
});

const submitting = ref<boolean>(false);

const stripeCard = ref<InstanceType<typeof CheckoutStripeCard>>();
const agreeToTerms = ref<boolean>(false);
const cards = ref<ICard[]>();
const individualAddressID = ref<number | null>(null);
const companyAddressID = ref<number | null>(null);

// Products state
const products = ref<IProduct[]>([]);
const productsSaving = ref<boolean>(false);
const productsLoaded = ref<boolean>(false);
const hasProducts = computed(() => {
  return products.value.length > 0;
});

const hasStripeGateway = computed(() => {
  return paymentGateways.value?.some((gw) => gw.kind === "stripe") ?? false;
});

const hasManualGateway = computed(() => {
  return paymentGateways.value?.some((gw) => gw.kind === "manual") ?? false;
});

const hasVippsGateway = computed(() => {
  return paymentGateways.value?.some((gw) => gw.kind === "vipps") ?? false;
});

const hasPaypalGateway = computed(() => {
  return paymentGateways.value?.some((gw) => gw.kind === "paypal") ?? false;
});

const hasKustomGateway = computed(() => {
  return paymentGateways.value?.some((gw) => gw.kind === "kustom") ?? false;
});

const selectedCourseSetting = computed<ICourseSetting | null>(() => {
  if (!course.value) return null;

  return (
    course.value?.course_settings.find(
      (cs) => cs.id === order.value?.booking_option_id
    ) ??
    availableCourseSettings.value[0] ??
    null
  );
});
const monthlyBilling = computed<boolean>({
  get() {
    return (
      selectedCourseSetting.value?.kind === "subscription" ||
      selectedCourseSetting.value?.kind === "individual_subscription"
    );
  },
  set(value) {
    if (!order.value) return;
    if (value) {
      if (
        selectedCourseSetting.value?.kind !== "subscription" &&
        selectedCourseSetting.value?.kind !== "individual_subscription"
      ) {
        const id = subscriptionCourseSetting.value?.id;
        if (id) order.value.booking_option_id = id;
      }
      return;
    }
    const id = course.value?.course_settings.find(
      (cs) =>
        cs.kind !== "subscription" && cs.kind !== "individual_subscription"
    )?.id;
    if (id) order.value.booking_option_id = id;
  },
});
const availableCourseSettings = computed(() => {
  if (!course.value) return [];

  // Always show all available course settings including subscription
  if (course.value.variety === "personal")
    return course.value.course_settings
      .filter((cs) =>
        ["subscription", "individual_subscription", "private_group"].includes(
          cs.kind
        )
      )
      .sort((a, b) => a.amount - b.amount);
  else
    return course.value.course_settings
      .filter((cs) =>
        [
          "subscription",
          "individual_subscription",
          "licence",
          "private_group",
        ].includes(cs.kind)
      )
      .sort((a, b) => a.amount - b.amount);
});
const subscriptionCourseSetting = computed(() => {
  if (!course.value) return null;
  const subs = course.value.course_settings.filter(
    (cs) => cs.kind === "subscription" || cs.kind === "individual_subscription"
  );
  // Only a monthly-billed subscription can act as the per-month baseline that
  // downstream discount math (`base_price * planMonths`) assumes. If no monthly
  // variant exists, return null so consumers skip discount enrichment instead
  // of mispricing against a quarterly/yearly plan.
  return (
    subs.find((cs) => !cs.subscription_interval || cs.subscription_interval === "month") ||
    null
  );
});
// const activeAddress = computed<IAddress | null>({
//   get() {
//     return (
//       availableAddresses.value.find((a) => a.id === order.address_id) || null
//     );
//   },
//   set(value) {
//     order.address_id = value?.id || null;
//   },
// });
const addressType = computed<"basic" | "individual" | "company">({
  get() {
    if (checkoutState.paymentGateway === "stripe" || checkoutState.paymentGateway === "vipps" || checkoutState.paymentGateway === "paypal" || checkoutState.paymentGateway === "kustom") return "basic";
    if (order.value?.invoice_type === "individual") return "individual";
    return "company";
  },
  set(value) {
    if (order.value)
      order.value.invoice_type = value === "company" ? "company" : "individual";
  },
});

const addressID = computed<number | null>({
  get() {
    if (addressType.value === "company") {
      return companyAddressID.value;
    } else {
      return individualAddressID.value;
    }
  },
  set(value) {
    if (addressType.value === "company") companyAddressID.value = value;
    else individualAddressID.value = value;
  },
});

watch(
  () => order.value?.address_id,
  (newV) => {
    if (newV !== undefined && addressID.value !== newV) addressID.value = newV;
  }
);

watch(addressType, (v) => {
  if (order.value && v === "basic") order.value.invoice_type = "individual";
});

watch(addressID, (v) => {
  if (order.value) order.value.address_id = v;
});

const selectedCardToken = computed<string | null | undefined>({
  get() {
    if (order.value?.card_token) return order.value.card_token;
    if (!cards.value) return null;
    // initialize with existing card
    if (order.value?.card_token === undefined && cards.value.length > 0)
      return cards.value[0].id;

    return order.value?.card_token ?? null;
  },
  set(value) {
    if (value === order.value?.card_token) return;

    if (order.value) order.value.card_token = value;
  },
});

async function updateOrder(newData: Partial<IOrder>) {
  if (!checkoutState.loaded) return;
  await updateCourseOrder(newData);
}

watch(
  () => order.value?.group_id,
  (newData, oldData) => {
    if (oldData === undefined) return;
    if (newData === oldData) return;
    if (!checkoutState.loaded) return;
    updateOrder({ group_id: newData });
  }
);

watch(
  () => order.value?.coupon_code,
  (newData, oldData) => {
    if (oldData === undefined) return;
    if (newData === oldData) return;
    if (!checkoutState.loaded) return;
    updateOrder({ coupon_code: newData });
  }
);

watch(
  () => order.value?.booking_option_id,
  (newData, oldData) => {
    if (oldData === undefined) return;
    if (newData === oldData) return;
    if (!checkoutState.loaded) return;
    updateOrder({ booking_option_id: newData });
  }
);
watch(
  () => order.value?.start_date,
  (newData, oldData) => {
    if (oldData === undefined) return;
    if (newData === oldData) return;
    if (!checkoutState.loaded) return;
    updateOrder({ start_date: newData });
  }
);
watch(subscriptionCourseSetting, (value, oldValue) => {
  // Only switch away from monthly billing when a subscription option was previously
  // available but got removed. Avoid overriding booking_option_id on initial course load
  // where the computed goes from null (no course) to undefined (no subscription settings).
  if (oldValue && !value) monthlyBilling.value = false;
});
watch(monthlyBilling, (value) => {
  if (value) checkoutState.paymentGateway = "stripe";
});

// Fetch cards only when Stripe gateway is available
watch(hasStripeGateway, (enabled) => {
  if (enabled) {
    getCardsList()
      .then((cardsList) => {
        cards.value = cardsList;
      })
      .catch((error) => {
        console.error("Error loading cards:", error);
      });
  } else {
    cards.value = undefined;
  }
}, { immediate: true });

onMounted(async () => {
  fetchOrder();
  fetchCourse();
  fetchPaymentGateways();
  fetchUserProfile();
  Promise.all([
    getCourseGroups(route.params.course_id as string),
    getCourseProducts(route.params.course_id as string),
  ])
    .then(([remoteGroups, courseProducts]) => {
      groups.value = remoteGroups;
      products.value = courseProducts;
      productsLoaded.value = true;
    })
    .then(() => {
      checkoutState.loaded = true;
    })
    .catch((error) => {
      console.error("Error loading products:", error);
    });

  if (route.query.payment_redirect === "vipps") {
    router.replace({ query: { ...route.query, payment_redirect: undefined } });
    completeVippsPayment();
  }

  if (route.query.payment_redirect === "paypal") {
    router.replace({ query: { ...route.query, payment_redirect: undefined } });
    completePaypalPayment();
  }

  if (route.query.payment_redirect === "kustom") {
    if (route.query.kustom_error) {
      const map: Record<string, string> = {
        cancelled: "kustom_payment_cancelled",
        declined: "kustom_payment_declined",
        error: "kustom_payment_error",
      };
      const key = map[route.query.kustom_error as string] ?? "kustom_payment_error";
      checkoutState.paymentError = t(key);
      const q = { ...route.query };
      delete q.payment_redirect;
      delete q.kustom_session_id;
      delete q.kustom_error;
      router.replace({ path: route.path, query: q });
    } else {
      const q = { ...route.query };
      delete q.payment_redirect;
      delete q.kustom_session_id;
      delete q.kustom_error;
      await router.replace({ path: route.path, query: q });
      completeKustomPayment();
    }
  }

});

// Add a product to the order
async function addProductToOrder(product: IProduct) {
  if (!order.value) return;
  productsSaving.value = true;

  const newOrderLine: IOrderLine = {
    id: null,
    orderable_id: product.id,
    orderable_type: "Product",
    price_in_cents: product.price_in_cents,
    product: product,
  };

  const currentLines = order.value.order_lines || [];
  const updatedLines = [...currentLines, newOrderLine];

  try {
    await updateCourseOrder({
      order_lines_attributes: updatedLines.map((line) => ({
        id: line.id,
        orderable_id: line.orderable_id,
        orderable_type: line.orderable_type,
        _destroy: line._destroy,
      })),
    } as Partial<IOrder>);
  } catch (e) {
    console.error("Failed to add product", e);
  } finally {
    productsSaving.value = false;
  }
}

// Swap a product variant for another in the same group, in a single round-trip.
async function swapProductInOrder({ from, to }: { from: IProduct; to: IProduct }) {
  if (!order.value) return;
  productsSaving.value = true;

  const fromLine = order.value.order_lines?.find(
    (line) => line.orderable_id === from.id && !line._destroy
  );

  const lines: Array<{
    id: number | null;
    orderable_id: number;
    orderable_type: "Product";
    _destroy?: boolean;
  }> = [];

  if (fromLine?.id) {
    lines.push({
      id: fromLine.id,
      orderable_id: fromLine.orderable_id,
      orderable_type: fromLine.orderable_type,
      _destroy: true,
    });
  }
  lines.push({
    id: null,
    orderable_id: to.id,
    orderable_type: "Product",
  });

  try {
    await updateCourseOrder({
      order_lines_attributes: lines,
    } as Partial<IOrder>);
  } catch (e) {
    console.error("Failed to swap product variant", e);
  } finally {
    productsSaving.value = false;
  }
}

// Remove a product from the order
async function removeProductFromOrder(product: IProduct) {
  if (!order.value) return;
  productsSaving.value = true;

  const lineToRemove = order.value.order_lines?.find(
    (line) => line.orderable_id === product.id && !line._destroy
  );

  if (!lineToRemove) {
    productsSaving.value = false;
    return;
  }

  try {
    if (lineToRemove.id) {
      // Mark for destruction if it exists in the database
      await updateCourseOrder({
        order_lines_attributes: [
          {
            id: lineToRemove.id,
            orderable_id: lineToRemove.orderable_id,
            orderable_type: lineToRemove.orderable_type,
            _destroy: true,
          },
        ],
      } as Partial<IOrder>);
    } else {
      // Just remove from local state if not persisted
      const updatedLines = order.value.order_lines.filter(
        (line) => line.orderable_id !== product.id
      );
      await updateCourseOrder({
        order_lines_attributes: updatedLines.map((line) => ({
          id: line.id,
          orderable_id: line.orderable_id,
          orderable_type: line.orderable_type,
          _destroy: line._destroy,
        })),
      } as Partial<IOrder>);
    }
  } catch (e) {
    console.error("Failed to remove product", e);
  } finally {
    productsSaving.value = false;
  }
}

function addPaymentRedirectParam(url: string, provider: string): string {
  const u = new URL(url);
  u.searchParams.set("payment_redirect", provider);
  return u.toString();
}

function applyGeneralFailure(result: { result: Record<string, unknown> }) {
  const r = result.result;
  checkoutState.paymentError = (r.message as string) ?? null;
  const cls = r.error_class as string | undefined;
  const msg = r.error_message as string | undefined;
  const backtrace = r.backtrace as string[] | undefined;
  if (cls || msg || backtrace?.length) {
    const head = [cls, msg].filter(Boolean).join(": ");
    checkoutState.paymentErrorDetails = [head, ...(backtrace ?? [])]
      .filter(Boolean)
      .join("\n");
  } else {
    checkoutState.paymentErrorDetails = null;
  }
}

async function completeVippsPayment() {
  submitting.value = true;
  checkoutState.paymentGateway = "vipps";
  checkoutState.paymentError = null;
  checkoutState.paymentErrorDetails = null;

  const result = await submitOrder(
    route.params.course_id as string,
    route.params.order_uid as string,
    {
      card_token: null,
      payment_gateway: "vipps",
      return_url: window.location.href,
    }
  );

  if (result.type === "success") {
    location.href = result.redirect_path;
    return;
  }
  if ((result as any).type === "redirect" && (result as any).redirect_url) {
    // We just returned from a Vipps redirect. Getting another redirect means
    // the previous payment failed and a new session was created. Show error
    // instead of following the redirect in an infinite loop.
    checkoutState.paymentError = t("vipps_payment_failed");
    submitting.value = false;
    return;
  }
  if (result.type === "general_failure") {
    applyGeneralFailure(result as { result: Record<string, unknown> });
  }
  submitting.value = false;
}

async function completePaypalPayment() {
  submitting.value = true;
  checkoutState.paymentGateway = "paypal";
  checkoutState.paymentError = null;
  checkoutState.paymentErrorDetails = null;

  const result = await submitOrder(
    route.params.course_id as string,
    route.params.order_uid as string,
    {
      card_token: null,
      payment_gateway: "paypal",
      return_url: window.location.href,
    }
  );

  if (result.type === "success") {
    location.href = result.redirect_path;
    return;
  }
  if ((result as any).type === "redirect" && (result as any).redirect_url) {
    // We just returned from a PayPal redirect. Getting another redirect means
    // the previous payment failed and a new session was created. Show error
    // instead of following the redirect in an infinite loop.
    checkoutState.paymentError = t("paypal_payment_failed");
    submitting.value = false;
    return;
  }
  if (result.type === "general_failure") {
    applyGeneralFailure(result as { result: Record<string, unknown> });
  }
  submitting.value = false;
}

async function completeKustomPayment() {
  submitting.value = true;
  checkoutState.paymentGateway = "kustom";
  checkoutState.paymentError = null;
  checkoutState.paymentErrorDetails = null;

  const result = await submitOrder(
    route.params.course_id as string,
    route.params.order_uid as string,
    {
      card_token: null,
      payment_gateway: "kustom",
      return_url: addPaymentRedirectParam(window.location.href, "kustom"),
    }
  );

  if (result.type === "success") {
    location.href = result.redirect_path;
    return;
  }
  if ((result as any).type === "redirect" && (result as any).redirect_url) {
    // We just returned from a Kustom redirect. Getting another redirect means
    // the previous payment failed and a new session was created. Show error
    // instead of following the redirect in an infinite loop.
    checkoutState.paymentError = t("kustom_payment_error");
    submitting.value = false;
    return;
  }
  if (result.type === "general_failure") {
    applyGeneralFailure(result as { result: Record<string, unknown> });
  }
  submitting.value = false;
}

async function payWithCard() {
  checkoutState.paymentError = null;
  checkoutState.paymentErrorDetails = null;
  if (!agreeToTerms.value) {
    checkoutState.paymentError = "Please agree to the terms and conditions";
    return;
  }
  if (!user_profile.value) {
    checkoutState.paymentError = "Please login to continue";
    return;
  }
  if (submitting.value) return;
  submitting.value = true;
  if (!selectedCardToken.value && checkoutState.paymentGateway === "stripe") {
    const result = await stripeCard.value?.getToken();
    if (result.error) {
      submitting.value = false;
      checkoutState.paymentError = result.error.message;
      return;
    }
    try {
      const newCard = await createCard(result.token.id);
      cards.value = [...(cards.value || []), newCard];
      selectedCardToken.value = newCard.id;
    } catch (err) {
      submitting.value = false;
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const error = err as AxiosError<{
          error: string;
        }>;
        checkoutState.paymentError = error.response?.data?.error || null;
      }
      return;
    }
    getCardsList().then((cardsList) => {
      cards.value = cardsList;
    });
  }

  try {
    const address = await saveAddress();
    if (order.value && address) await updateOrder({ address_id: address.id });
  } catch (e) {
    submitting.value = false;
    checkoutState.paymentError = "Error creating address";
    return;
  }

  const result = await submitOrder(
    route.params.course_id as string,
    route.params.order_uid as string,
    {
      card_token: selectedCardToken.value,
      payment_gateway: checkoutState.paymentGateway,
      return_url: addPaymentRedirectParam(window.location.href, checkoutState.paymentGateway),
    }
  );
  if ((result as any).type === "redirect" && (result as any).redirect_url) {
    window.location.href = (result as any).redirect_url;
    return;
  }
  // console.log(`${result.type}:`);
  if (result.type === "success") {
    // submitting.value = false;
    location.href = result.redirect_path;
    return;
  }
  if (
    result.type === "requires_action" ||
    result.type === "requires_confirmation"
  ) {
    // console.log("Stripe confirmCardPayment");
    // console.log(result.result);
    stripe.value
      ?.confirmCardPayment(result.result.stripe_payment_intent.client_secret)
      .then((resp) => {
        if (resp.error) {
          checkoutState.paymentError = resp.error.message ?? null;
        } else {
          location.href = result.redirect_path;
        }
        submitting.value = false;
      });
    return;
  }
  if (result.type === "requires_payment_method") {
    console.log(result.result);

    submitting.value = false;
    return;
  }
  if (result.type === "general_failure") {
    applyGeneralFailure(result as { result: Record<string, unknown> });
    submitting.value = false;
    return;
  }
  // debugger;
  submitting.value = false;
  // .catch((err) => {
  //   submitting.value = false;
  //   if (axios.isAxiosError(err) && err.response?.status === 422) {
  //     const error = err as AxiosError<{
  //       error: string;
  //     }>;
  //     checkoutState.paymentError = error.response?.data?.error || null;
  //   }
  // });
  // submitting.value = false;
}

// Load Stripe only when the gateway is available and a key is configured
watch(hasStripeGateway, (enabled) => {
  if (enabled && gon.stripe_key) {
    loadStripe(gon.stripe_key, {
      locale: locale.value as StripeConstructorOptions["locale"],
    }).then((s) => {
      stripe.value = s;
    });
  }
}, { immediate: true });
</script>

<i18n lang="yaml">
en:
  booking: "Booking"
  back: "Back"
  loading: "Loading..."
  first_name: "First name"
  last_name: "Last name"
  participant_information: Fill in the details of the course participant
  email: "Email"
  mobile_number: Mobile number
  next: "Next"
  payment: Payment
  monthly_billing: Monthly billing
  pay_with_credit_card: Pay with credit card
  address: Address
  postal_code: Postal code
  city: City
  country: Country
  agree_terms_label: I agree to the {link}.
  terms_and_conditions: Terms and conditions
  credit_card: Credit card
  invoice: Invoice
  vipps: Vipps
  pay_with_invoice: Pay via invoice (bank transfer)
  pay_with_vipps: Pay with Vipps
  pay_with_paypal: Pay with PayPal
  pay_with_kustom: Pay with Kustom
  vipps_redirect_notice: You will be redirected to Vipps to complete your payment.
  vipps_payment_failed: Your Vipps payment was not completed. Please try again or choose a different payment method.
  paypal_redirect_notice: You will be redirected to PayPal to complete your payment.
  paypal_payment_failed: Your PayPal payment was not completed. Please try again or choose a different payment method.
  kustom_payment_cancelled: Payment was cancelled. Please try again.
  kustom_payment_declined: Payment was declined. Please choose a different payment method.
  kustom_payment_error: Something went wrong with the payment. Please try again.
  company_name: Company name
  contact_person: Contact person
  send_invoice: Send invoice
  add_new_card: Add new credit card
  payment_method: Payment method
  select_payment_card: Select payment card
  agree_to_terms_aria: I agree to the terms and conditions
  back_to_profile: Back to profile information
  back_to_options: Back to price and add-ons
  pay: Pay
  select_price_and_addons: Select price and add-ons
  continue: Continue
  continue_to_payment: Continue to payment
nb:
  booking: "Påmelding"
  back: "Tilbake"
  loading: "Laster..."
  first_name: "Fornavn"
  last_name: "Etternavn"
  participant_information: Informasjon om kursdeltaker
  email: "E-post"
  mobile_number: Mobilnummer
  next: "Neste"
  payment: Betaling
  monthly_billing: Månedlig betaling
  pay_with_credit_card: Betal med kredittkort
  address: Adresse
  postal_code: Postnummer
  city: By
  country: Land
  agree_terms_label: Jeg godtar {link}.
  terms_and_conditions: Vilkår og betingelser
  credit_card: Kredittkort
  invoice: Faktura
  vipps: Vipps
  pay_with_invoice: Betal via faktura (bankoverføring)
  pay_with_vipps: Betal med Vipps
  pay_with_paypal: Betal med PayPal
  pay_with_kustom: Betal med Kustom
  vipps_redirect_notice: Du blir videresendt til Vipps for å fullføre betalingen.
  vipps_payment_failed: Vipps-betalingen ble ikke fullført. Vennligst prøv igjen eller velg en annen betalingsmetode.
  paypal_redirect_notice: Du blir videresendt til PayPal for å fullføre betalingen.
  paypal_payment_failed: PayPal-betalingen ble ikke fullført. Vennligst prøv igjen eller velg en annen betalingsmetode.
  kustom_payment_cancelled: Betalingen ble avbrutt. Vennligst prøv igjen.
  kustom_payment_declined: Betalingen ble avvist. Velg en annen betalingsmåte.
  kustom_payment_error: Noe gikk galt med betalingen. Vennligst prøv igjen.
  company_name: Bedriftsnavn
  contact_person: Kontaktperson
  send_invoice: Bekreft påmelding med faktura
  add_new_card: Legg til nytt kredittkort
  payment_method: Betalingsmetode
  select_payment_card: Velg betalingskort
  agree_to_terms_aria: Jeg godtar vilkårene og betingelsene
  back_to_profile: Tilbake til profilinformasjon
  back_to_options: Tilbake til pris og tillegg
  pay: Betal
  select_price_and_addons: Velg pris og tillegg
  continue: Fortsett
  continue_to_payment: Fortsett til betaling
uk:
  booking: "Бронювання"
  back: "Назад"
  loading: "Завантаження..."
  first_name: "Ім'я"
  last_name: "Прізвище"
  participant_information: Заповніть дані про учасника курсу
  email: "Електронна пошта"
  mobile_number: Мобільний номер
  next: "Далі"
  payment: Оплата
  monthly_billing: Місячний розрахунок
  pay_with_credit_card: Оплатити кредитною карткою
  address: Адреса
  postal_code: Поштовий індекс
  city: Місто
  country: Країна
  agree_terms_label: Я погоджуюсь з {link}.
  terms_and_conditions: Умовами та положеннями
  credit_card: Кредитна картка
  invoice: Рахунок
  vipps: Vipps
  pay_with_invoice: Оплатити рахунком (банківський переказ)
  pay_with_vipps: Оплатити через Vipps
  pay_with_paypal: Оплатити через PayPal
  pay_with_kustom: Сплатити через Kustom
  vipps_redirect_notice: Вас буде перенаправлено до Vipps для завершення оплати.
  vipps_payment_failed: Оплату через Vipps не завершено. Спробуйте ще раз або виберіть інший спосіб оплати.
  paypal_redirect_notice: Вас буде перенаправлено до PayPal для завершення оплати.
  paypal_payment_failed: Оплату через PayPal не завершено. Спробуйте ще раз або виберіть інший спосіб оплати.
  kustom_payment_cancelled: Платіж було скасовано. Спробуйте ще раз.
  kustom_payment_declined: Платіж відхилено. Оберіть інший спосіб оплати.
  kustom_payment_error: Щось пішло не так із платежем. Спробуйте ще раз.
  company_name: Назва компанії
  contact_person: Контактна особа
  send_invoice: Надіслати рахунок
  add_new_card: Додати нову кредитну картку
  payment_method: Спосіб оплати
  select_payment_card: Виберіть платіжну картку
  agree_to_terms_aria: Я погоджуюсь з умовами та положеннями
  back_to_profile: Назад до інформації профілю
  back_to_options: Назад до ціни та додатків
  pay: Оплатити
  select_price_and_addons: Виберіть ціну та додатки
  continue: Продовжити
  continue_to_payment: Перейти до оплати
es:
  booking: "Inscripción"
  back: "Volver"
  loading: "Cargando..."
  first_name: "Nombre"
  last_name: "Apellido"
  participant_information: Rellene los datos del participante del curso
  email: "Correo electrónico"
  mobile_number: Número de teléfono móvil
  next: "Siguiente"
  payment: Pago
  monthly_billing: Facturación mensual
  pay_with_credit_card: Pagar con tarjeta de crédito
  address: Dirección
  postal_code: Código postal
  city: Ciudad
  country: País
  agree_terms_label: Estoy de acuerdo con los {link}.
  terms_and_conditions: Términos y condiciones
  credit_card: Tarjeta de crédito
  invoice: Factura
  vipps: Vipps
  pay_with_invoice: Pagar por factura (transferencia bancaria)
  pay_with_vipps: Pagar con Vipps
  pay_with_paypal: Pagar con PayPal
  pay_with_kustom: Pagar con Kustom
  vipps_redirect_notice: Serás redirigido a Vipps para completar el pago.
  vipps_payment_failed: Tu pago con Vipps no se completó. Intenta de nuevo o elige otro método de pago.
  paypal_redirect_notice: Serás redirigido a PayPal para completar el pago.
  paypal_payment_failed: Tu pago con PayPal no se completó. Intenta de nuevo o elige otro método de pago.
  kustom_payment_cancelled: El pago fue cancelado. Vuelve a intentarlo.
  kustom_payment_declined: El pago fue rechazado. Elige otro método de pago.
  kustom_payment_error: Algo salió mal con el pago. Vuelve a intentarlo.
  company_name: Nombre de la empresa
  contact_person: Persona de contacto
  send_invoice: Enviar factura
  add_new_card: Agregar nueva tarjeta de crédito
  payment_method: Método de pago
  select_payment_card: Seleccionar tarjeta de pago
  agree_to_terms_aria: Estoy de acuerdo con los términos y condiciones
  back_to_profile: Volver a la información del perfil
  back_to_options: Volver a precio y complementos
  pay: Pagar
  select_price_and_addons: Seleccionar precio y complementos
  continue: Continuar
  continue_to_payment: Continuar al pago
pl:
  booking: "Rezerwacja"
  back: "Wstecz"
  loading: "Ładowanie..."
  first_name: "Imię"
  last_name: "Nazwisko"
  participant_information: Wypełnij dane uczestnika kursu
  email: "E-mail"
  mobile_number: Numer telefonu komórkowego
  next: "Dalej"
  payment: Płatność
  monthly_billing: Rozliczenie miesięczne
  pay_with_credit_card: Zapłać kartą kredytową
  address: Adres
  postal_code: Kod pocztowy
  city: Miasto
  country: Kraj
  agree_terms_label: Zgadzam się z {link}.
  terms_and_conditions: Warunkami i zasadami
  credit_card: Karta kredytowa
  invoice: Faktura
  vipps: Vipps
  pay_with_invoice: Zapłać fakturą (przelew bankowy)
  pay_with_vipps: Zapłać przez Vipps
  pay_with_paypal: Zapłać przez PayPal
  pay_with_kustom: Zapłać przez Kustom
  vipps_redirect_notice: Zostaniesz przekierowany do Vipps, aby dokończyć płatność.
  vipps_payment_failed: Płatność Vipps nie została ukończona. Spróbuj ponownie lub wybierz inną metodę płatności.
  paypal_redirect_notice: Zostaniesz przekierowany do PayPal, aby dokończyć płatność.
  paypal_payment_failed: Płatność PayPal nie została ukończona. Spróbuj ponownie lub wybierz inną metodę płatności.
  kustom_payment_cancelled: Płatność została anulowana. Spróbuj ponownie.
  kustom_payment_declined: Płatność została odrzucona. Wybierz inną metodę.
  kustom_payment_error: Coś poszło nie tak z płatnością. Spróbuj ponownie.
  company_name: Nazwa firmy
  contact_person: Osoba kontaktowa
  send_invoice: Wyślij fakturę
  add_new_card: Dodaj nową kartę kredytową
  payment_method: Sposób płatności
  select_payment_card: Wybierz kartę płatniczą
  agree_to_terms_aria: Zgadzam się z warunkami i zasadami
  back_to_profile: Wróć do informacji o profilu
  back_to_options: Wróć do ceny i dodatków
  pay: Zapłać
  select_price_and_addons: Wybierz cenę i dodatki
  continue: Kontynuuj
  continue_to_payment: Przejdź do płatności
</i18n>
